import { NodeResult, RunResults } from "@custom-types/modelRun"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import { color_tones, lighter_rgba, to_rgba } from "@features/results/Result/const"
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SxProps,
  Typography,
} from "@mui/material"
import Button from "@mui/material/Button"
import {
  Chart,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js"
import zoomPlugin from "chartjs-plugin-zoom"
import { useEffect, useMemo, useRef, useState } from "react"
import { Scatter } from "react-chartjs-2"
import { formatValuePythonic, getFormatPrecision } from "@utils/format/pythonicFormat"

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend, zoomPlugin)

type ScatterRawResultsProps = {
  rawResultDataFilterNodes: RunResults
  lagShift: number
  node: GroupNodeLabel | null
  onlyChart?: boolean
  maintainAspectRatio?: boolean
  chartContainerSx?: SxProps
  sx?: SxProps
  legendPosition?: "top" | "bottom"
  showLegend?: boolean
  hiddenDatasets?: string[]
  onLegendClick?: (datasetLabel: string) => void
}

export const ScatterRawResultsPlot = ({
  rawResultDataFilterNodes,
  lagShift,
  node,
  onlyChart = false,
  maintainAspectRatio = false,
  chartContainerSx = { maxHeight: 500, mb: 3 },
  sx = { my: 2 },
  legendPosition = "top",
  showLegend = true,
  hiddenDatasets = [],
  onLegendClick,
}: ScatterRawResultsProps) => {
  const [selectedTimeSteps, setSelectedTimeSteps] = useState<string[]>([])
  const chartRef = useRef<Chart<"scatter", unknown[], unknown>>()
  const chartContainerRef = useRef<HTMLDivElement | null>(null)
  const [maxXRange, setMaxXRange] = useState(0)

  const nodeResults = useMemo(
    () =>
      rawResultDataFilterNodes
        ? Object.values(rawResultDataFilterNodes).reduce(
            (res, curr) => [...res, ...Object.values(curr)],
            [] as NodeResult[][],
          )
        : [],
    [rawResultDataFilterNodes],
  )

  const runIds = Object.keys(rawResultDataFilterNodes)

  const preparedData = useMemo(() => {
    const result = nodeResults.reduce((acc, item) => {
      // guarantee data is orderly
      const sorted = item.slice().sort((a, b) => (a.timestep > b.timestep ? 1 : -1))
      sorted.forEach((el, index) => {
        if (index >= lagShift) {
          acc[el.timestep] = el.data
        }
      })

      return acc
    }, {} as { [key: string]: number[] })

    setSelectedTimeSteps([Object.keys(result)[0]?.toString() || ""])
    return result
  }, [lagShift, nodeResults])

  const apply_lighter = function (color_tones: number[][], color_index: number) {
    const apply_number_times = Math.floor(color_index / color_tones.length)
    let color_to_apply = color_tones[color_index % color_tones.length]
    for (let i = 0; i < apply_number_times; i++) {
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      color_to_apply = lighter_rgba(color_to_apply, 30)
    }

    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    return to_rgba(color_to_apply)
  }

  const datasets = useMemo(() => {
    const tempDatasets: {
      label: string
      backgroundColor: string
      data: { x: string; y: number }[]
      hidden?: boolean
    }[] = []
    let maxXindex = 0
    let color_index = 0

    // eslint-disable-next-line guard-for-in -- FIX: check for unwanted properties or refactor to for..of
    for (const year in preparedData) {
      const data: { x: string; y: number }[] = []
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      maxXindex = maxXindex < preparedData[year].length ? preparedData[year].length : maxXindex
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      preparedData[year].forEach((item, index) => {
        selectedTimeSteps.includes(year) && data.push({ x: `${index}`, y: item })
      })

      tempDatasets.push({
        label: year,
        backgroundColor: apply_lighter(color_tones, color_index++),
        data,
      })
    }

    return tempDatasets
  }, [preparedData, selectedTimeSteps])

  useEffect(() => {
    let maxXindex = 0

    // eslint-disable-next-line guard-for-in -- FIX: check for unwanted properties or refactor to for..of
    for (const year in preparedData) {
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      maxXindex = maxXindex < preparedData[year].length ? preparedData[year].length : maxXindex
    }
    setMaxXRange(maxXindex)
  }, [preparedData])
  const chartData = useMemo(
    () => ({
      labels: [...Array(maxXRange).keys()],
      datasets: datasets.filter((x) => x.data.length > 0),
    }),

    [datasets, maxXRange],
  )

  // Apply hidden state to datasets based on hiddenDatasets prop
  chartData.datasets.forEach((dataset) => {
    if (dataset.label) {
      dataset.hidden = hiddenDatasets?.includes(dataset.label) ?? false
    }
  })

  const precision = getFormatPrecision(node?.format)
  const ticksBlock =
    precision != -1
      ? {
          callback: function (value: number) {
            return formatValuePythonic(value, node?.format)
          },
          precision: precision,
        }
      : {
          callback: function (value: number) {
            return formatValuePythonic(value, node?.format)
          },
        }

  const scatterChartOptons: ChartOptions<"scatter"> = {
    responsive: true,
    maintainAspectRatio,
    plugins: {
      legend: {
        position: legendPosition,
        display: showLegend,
        onClick: onLegendClick
          ? function (event, legendItem, legend) {
              const index = legendItem.datasetIndex
              if (index === undefined) return
              const dataset = legend.chart.data.datasets[index]
              const datasetLabel = dataset?.label
              if (datasetLabel) {
                onLegendClick(datasetLabel)
              }
            }
          : undefined,
      },
      zoom: {
        limits: {
          x: { minRange: 2, min: 0, max: maxXRange },
        },
        zoom: {
          drag: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
      },
    },
    scales: {
      x: {
        min: 0,
        max: maxXRange,
      },
      y: {
        // @ts-ignore TODO kk: fix type
        ticks: ticksBlock,
      },
    },
  }

  const handleResetZoom = () => {
    if (chartRef && chartRef.current) {
      chartRef.current.resetZoom()
    }
  }

  return (
    <Grid container={true} sx={sx}>
      <Grid item={true} xs={3} sx={{ my: 1 }}>
        <FormControl fullWidth={true}>
          <InputLabel id="agg-label">Selected timesteps</InputLabel>
          <Select
            multiple
            labelId="agg-label"
            value={selectedTimeSteps}
            disabled={Object.keys(preparedData).length === 0}
            label="Selected timesteps"
            onChange={(value) =>
              setSelectedTimeSteps(
                Array.isArray(value.target.value) ? value.target.value : [value.target.value],
              )
            }
            size={"small"}
          >
            <MenuItem value={""}>&nbsp;</MenuItem>
            {Object.keys(preparedData).map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {!onlyChart && (
        <Grid item={true} xs={9} sx={{ mb: 2 }}>
          <Button onClick={handleResetZoom} variant={"contained"} sx={{ ml: 2 }}>
            Reset zoom
          </Button>
          <DownloadChartImageDialog
            buttonProps={{ sx: { ml: 1 } }}
            chartRef={chartContainerRef}
            defaultFileName="Scatter Plot"
            runReference={runIds.join(" vs. ")}
          />
        </Grid>
      )}
      <Grid item={true} xs={12} ref={chartContainerRef} sx={sx}>
        {!onlyChart && (
          <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
            Data scatter for <b>{node?.label || ""}</b>
          </Typography>
        )}
        <Box sx={chartContainerSx}>
          <Scatter options={scatterChartOptons} data={chartData} ref={chartRef} />
        </Box>
      </Grid>
    </Grid>
  )
}
